import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle, CheckCircle2, Clock, Calendar, ArrowRight } from "lucide-react";

/**
 * Re-Screen Watchlist (Coach Dashboard widget).
 *
 * Surfaces every roster athlete past the 90-day re-screen window so coaches
 * can drive screening compliance from one glance. Sorted: never → overdue →
 * due_soon → current, oldest first inside each bucket.
 */
const STATUS_STYLE = {
  overdue:  { fg: "#FCA5A5", bg: "rgba(220,38,38,0.15)",  border: "rgba(220,38,38,0.45)",  label: "Overdue" },
  due_soon: { fg: "#FBBF24", bg: "rgba(245,158,11,0.15)", border: "rgba(245,158,11,0.40)", label: "Due soon" },
  current:  { fg: "#6EE7B7", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.35)", label: "Current"  },
  never:    { fg: "#67E8F9", bg: "rgba(34,211,238,0.10)", border: "rgba(34,211,238,0.35)", label: "Never logged" },
};

export default function RescreenWatchlistCard() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("needs_attention");  // 'needs_attention' | 'all'

  useEffect(() => {
    api.get("/dashboard/rescreen-watchlist").then(({ data }) => setData(data));
  }, []);

  if (!data) return null;
  const counts = data.counts || {};
  const flagged = (data.athletes || []).filter((a) =>
    filter === "all" ? true : a.rescreen_status !== "current"
  );
  const needsAttention = counts.overdue + counts.due_soon + counts.never;

  return (
    <div className="rounded-xl ink-card p-5" data-testid="rescreen-watchlist">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase flex items-center gap-1.5">
            <Calendar className="h-3 w-3" /> Re-Screen Watchlist
          </div>
          <h3 className="font-display font-bold text-lg text-white mt-1">
            {needsAttention === 0 ? (
              <>All <b className="text-emerald-300">{counts.total}</b> athletes up to date</>
            ) : (
              <><b className="text-red-300">{needsAttention}</b> athlete{needsAttention === 1 ? "" : "s"} need re-screening</>
            )}
          </h3>
          <div className="text-[11px] text-white/45 mt-0.5">ArmIQ recommends a re-screen every 3 months ({data.threshold_days}d).</div>
        </div>
        <div className="inline-flex rounded-lg border border-white/10 p-0.5 bg-white/[0.02]">
          {[["needs_attention", "Flagged"], ["all", "All"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wide rounded-md transition ${filter === k ? "bg-cyan-400/20 text-cyan-200" : "text-white/50 hover:text-white"}`}
              data-testid={`rescreen-filter-${k}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Count chips */}
      <div className="flex flex-wrap gap-2 mb-3">
        {["overdue", "due_soon", "never", "current"].map((k) => {
          const s = STATUS_STYLE[k];
          return (
            <div key={k} className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide flex items-center gap-1.5"
              style={{ backgroundColor: s.bg, color: s.fg, border: `1px solid ${s.border}` }}>
              <span>{s.label}</span>
              <span className="font-mono-num">{counts[k] || 0}</span>
            </div>
          );
        })}
      </div>

      {/* Athlete list */}
      <div className="max-h-64 overflow-y-auto rounded-lg border border-white/10">
        {flagged.length === 0 ? (
          <div className="p-6 text-center text-xs text-white/50">
            <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-400 mb-2" />
            Every athlete is within the {data.threshold_days}-day window.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-white/[0.03] sticky top-0">
              <tr className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">
                <th className="text-left px-3 py-2">Athlete</th>
                <th className="text-left px-3 py-2">Position</th>
                <th className="text-right px-3 py-2">Days ago</th>
                <th className="text-right px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Re-screen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {flagged.map((a) => {
                const s = STATUS_STYLE[a.rescreen_status];
                return (
                  <tr key={a.user_id} className="text-white/85 hover:bg-white/[0.02]" data-testid={`rescreen-row-${a.user_id}`}>
                    <td className="px-3 py-2 font-semibold text-white truncate max-w-[180px]">
                      {a.name}
                      {a.gird_deg && Number(a.gird_deg) >= 18 && (
                        <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wide px-1 py-0.5 rounded bg-red-500/15 text-red-300">
                          GIRD {a.gird_deg}°
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-white/55 truncate max-w-[120px]">{a.position || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-num">
                      {a.days_since_assessment !== null && a.days_since_assessment !== undefined
                        ? <span className="text-white/80">{a.days_since_assessment}d</span>
                        : <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
                        style={{ backgroundColor: s.bg, color: s.fg, border: `1px solid ${s.border}` }}>
                        {s.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link to={`/dashboard/player/${a.user_id}?tab=baseline`}
                        className="inline-flex items-center gap-1 text-xs font-bold hover:underline"
                        style={{ color: "var(--brand-secondary)" }}
                        data-testid={`rescreen-link-${a.user_id}`}>
                        Log <ArrowRight className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
